import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { EMPTY, Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import urlJoin from 'url-join';
import {
    RequestLicenseUserSet, RequestPermission, RequestPermissionSet, RequestPermissionTree, RequestRolePerm, RequestRolePermSet,
    ResponseLicenseUserSet, ResponsePermission, ResponsePermissionSet, ResponsePermissionTree, ResponseRolePerm, ResponseRolePermSet
} from '../../../features/admin/models/messages';
import { IAuthMenu, fwHttpClientUtils } from '../models';
import {
    RequestAccess, RequestDelete, RequestMenu, RequestUserPermission, RequestUserPermissionSet, RequestUserSummary,
    ResponseAccess, ResponseDelete, ResponseMenu, ResponseUserPermission, ResponseUserPermissionParent, ResponseUserPermissionSet, ResponseUserSummary
} from '../models/messages';
import { userRequest } from '../models/user';
import { BaseHttpReqRespService } from './base-http-req-resp.service';



type NewType = RequestAccess;

@Injectable({
  providedIn: 'root'
})

export class AuthorizationService extends BaseHttpReqRespService {

  readonly endpointUserMenu: string = 'authorization/menu';
  readonly endpointTree: string = 'authorization/permission-tree';
  readonly endpointRolePerm: string = 'authorization/permission-role';
  readonly endpointUserHasAccess: string = 'authorization/hasaccess';
  readonly endpointPermission: string = 'authorization/permission';
  readonly endpointUserSummary: string = 'users/summary';
  readonly endpointLicenseUser: string = 'licenses/user';
  readonly endpointUser: string = 'authorization/userAdd';

  readonly endpointUserPermission: string = 'authorization/user-permission';

  readonly accessRoutes = new Map<string, ResponseAccess>();
  readonly accessRoles = new Map<string, ResponseAccess>();
  readonly accessModules = new Map<string, ResponseAccess>();
  readonly accessAuthIds = new Map<string, ResponseAccess>();

  constructor(http: HttpClient
    , @Inject(OKTA_AUTH) private oauth: OktaAuth
    , private router: Router
    , @Inject('CONFIG_SETTINGS') configSettings: any) {
    super(http, oauth);

    const genSettings = configSettings[<any>'general'];

    this.baseUrl = genSettings[<any>'adminApiUrl'];
    this.apiKey = genSettings[<any>'apiKey'];

  }

  hasAccess$(req: NewType): Observable<ResponseAccess> {

    let resp: ResponseAccess = new ResponseAccess();
    resp.success = null;

    if ((req.route) && (req.route.length > 0) && this.accessRoutes.has(req.route)) {
      resp = this.accessRoutes.get(req.route)!;
    }

    else if ((req.roleIntAppCode) && (req.roleIntAppCode.length > 0) && this.accessRoles.has(req.roleIntAppCode)) {
      resp = this.accessRoles.get(req.roleIntAppCode)!;
    }

    else if ((req.moduleIntAppCode) && (req.moduleIntAppCode.length > 0) && this.accessModules.has(req.moduleIntAppCode)) {
      resp = this.accessModules.get(req.moduleIntAppCode)!;
    }

    else if ((req.authId) && (req.authId.length > 0) && this.accessAuthIds.has(req.authId)) {
      resp = this.accessAuthIds.get(req.authId)!;
    }

    if (resp.success != null)
      return of(resp);


    let token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointUserHasAccess);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseAccess>(url, httpOptions).pipe(
        map(model => {

          if ((req.route) && (req.route.length > 0)) {
            this.accessRoutes.set(req.route, model);
          }
          else if ((req.roleIntAppCode) && (req.roleIntAppCode.length > 0)) {
            this.accessRoles.set(req.roleIntAppCode, model);
          }
          else if ((req.moduleIntAppCode) && (req.moduleIntAppCode.length > 0)) {
            this.accessModules.set(req.moduleIntAppCode, model);
          }
          else if ((req.authId) && (req.authId.length > 0)) {
            this.accessAuthIds.set(req.authId, model);
          }

          return (model);
        })
      );
    }
    else {
      resp.success = false;
      resp.message = "Security check failed";

      return of(resp);
    }
  }

  menuGet$(req: RequestMenu): Observable<IAuthMenu[]> {

    let emptyMenu: IAuthMenu[] = [];

    const token = this.getToken();
    if (!token) {
      of(emptyMenu);
    }

    return from(this.oauth.isAuthenticated()).pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(emptyMenu);
        }

        let url = urlJoin(this.baseUrl, this.endpointUserMenu);

        const httpOptions = {
          headers: this.getHttpBearerHeaders(token ?? "")
          , params: fwHttpClientUtils.toHttpParams(req)
        };

        return this.http.get<ResponseMenu>(url, httpOptions).pipe(
          map(model => {
            return (model.data) ? model.data : emptyMenu;
          })
        );
      })
    );

  }

  pagePermissionsGet$(req: RequestUserPermission): Observable<ResponseUserPermission> {
    const resp: ResponseUserPermission = new ResponseUserPermission();

    let token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, 'authorization/user-permission');

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseUserPermission>(url, httpOptions).pipe(
        map(model => {
          return (model);
        })
      );
    }
    else {
      resp.success = false;
      resp.message = "Security check failed";

      return of(resp);
    }
  }


  licenseUserSet$(req: RequestLicenseUserSet): Observable<ResponseLicenseUserSet> {

    let emptyResp: ResponseLicenseUserSet = new ResponseLicenseUserSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointLicenseUser);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseLicenseUserSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }


  permissionGet$(req: RequestPermission): Observable<ResponsePermission> {

    let emptyResp: ResponsePermission = new ResponsePermission();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointPermission);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponsePermission>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }

  }

  permissionSet$(req: RequestPermissionSet): Observable<ResponsePermissionSet> {

    let emptyResp: ResponsePermissionSet = new ResponsePermissionSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointPermission);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponsePermissionSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  permissionUpdate$(req: RequestPermissionSet): Observable<ResponsePermissionSet> {

    let emptyResp: ResponsePermissionSet = new ResponsePermissionSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointPermission);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.put<ResponsePermissionSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  permissionDelete$(req: RequestDelete): Observable<ResponseDelete> {

    let emptyResp: ResponseDelete = new ResponseDelete();
    emptyResp.success = false;

    var token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointPermission);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.delete<ResponseDelete>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  permissionTreeGet$(req: RequestPermissionTree): Observable<ResponsePermissionTree> {

    let resp: ResponsePermissionTree = new ResponsePermissionTree();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointTree);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponsePermissionTree>(url, httpOptions);

    }
    else {
      return of(resp);
    }
  }


  rolePermissionGet$(req: RequestRolePerm): Observable<ResponseRolePerm> {

    let emptyResp: ResponseRolePerm = new ResponseRolePerm();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointRolePerm);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseRolePerm>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }

  }

  rolePermissionSet$(req: RequestRolePermSet): Observable<ResponseRolePermSet> {

    let emptyResp: ResponseRolePermSet = new ResponseRolePermSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointRolePerm);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseRolePermSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  userPermissionGet$(req: RequestUserPermission): Observable<ResponseUserPermission> {
    const resp: ResponseUserPermissionParent = new ResponseUserPermissionParent();

    let token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, 'authorization/user-permission');

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseUserPermission>(url, httpOptions).pipe(
        map(model => {
          return (model);
        })
      );
    }
    else {
      resp.success = false;
      resp.message = "Security check failed";

      return of(resp);
    }
  }

  userPermissionAdminGet$(req: RequestUserPermission): Observable<ResponseUserPermission> {
    const resp: ResponseUserPermissionParent = new ResponseUserPermissionParent();

    let token = this.getToken();

    if (token != null) {
      let url = urlJoin(this.baseUrl, 'authorization/user-permission-lookup');

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseUserPermissionParent>(url, httpOptions).pipe(
        map(model => {
          return (model);
        })
      );
    }
    else {
      resp.success = false;
      resp.message = "Security check failed";

      return of(resp);
    }
  }

  userPermissionSet$(req: RequestUserPermissionSet): Observable<ResponseUserPermissionSet> {

    let emptyResp: ResponseUserPermissionSet = new ResponseUserPermissionSet();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, 'authorization/user-permission');

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      return this.http.post<ResponseUserPermissionSet>(url, req, httpOptions);

    }
    else {
      return of(emptyResp);
    }
  }

  userSummaryGet$(req: RequestUserSummary): Observable<ResponseUserSummary> {

    let emptyResp: ResponseUserSummary = new ResponseUserSummary();

    let token = this.getToken();
    if (token != null) {
      let url = urlJoin(this.baseUrl, this.endpointUserSummary);

      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
        , params: fwHttpClientUtils.toHttpParams(req)
      };

      return this.http.get<ResponseUserSummary>(url, httpOptions);

    }
    else {
      return of(emptyResp);
    }

  }
  userPermissionUpdate$(req: userRequest): Observable<any> {
    let token = this.getToken();
    const url = this.baseUrl + this.endpointUser;
    if (token != undefined) {
      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)

      };
      return this.http.post(url, req, httpOptions)
    }
    else {
      return EMPTY
    }
  }

  // Should be called when the user logs off
  clearAuths() {

    this.accessRoutes.clear();
    this.accessRoles.clear();
    this.accessModules.clear();
    this.accessAuthIds.clear();
  }

  protected getToken(): string | undefined {
    return this.oauth.getAccessToken();
  }
}
